import React, { useCallback, useEffect, useMemo, useState } from "react";
import { signalsApi, intakeApi } from "@/lib/api";
import { toast } from "sonner";
import { GitBranch, Sparkles, Eye, X, ArrowUpRight, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

const STATUS_TABS = [
  { key: "new", label: "New" },
  { key: "reviewed", label: "Reviewed" },
  { key: "routed", label: "Routed" },
  { key: "dismissed", label: "Dismissed" },
];

function ConfidenceBadge({ value }) {
  const pct = Math.round((value || 0) * 100);
  const tone = pct >= 70 ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
    : pct >= 55 ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
    : "bg-white/10 text-white/60 border-white/20";
  return (
    <span data-testid="signal-confidence"
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ${tone}`}>
      {pct}% match
    </span>
  );
}

function SignalCard({ signal, onReview, onDismiss, onRoute, working }) {
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <div data-testid={`signal-card-${signal.id}`}
         className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-4 hover:border-white/20 transition-colors">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="border-white/15 text-white/70 bg-white/5">
              <Sparkles className="w-3 h-3 mr-1" />
              Cross-entity signal
            </Badge>
            <ConfidenceBadge value={signal.confidence} />
            <Badge variant="outline" className="border-white/15 text-white/50 text-[10px]">
              {signal.status}
            </Badge>
          </div>
          <h3 className="text-lg font-medium text-white">
            May also matter to <span className="text-white">{signal.target_entity_name || signal.target_entity_id}</span>
            {signal.target_project_name && (
              <> · <span className="text-white/70">{signal.target_project_name}</span></>
            )}
          </h3>
          <p className="text-xs text-white/50">
            Submitted through {signal.source_entity_name || "—"}
            {signal.submitted_by_name && <> by {signal.submitted_by_name}</>}
            {" · "}
            {new Date(signal.created_at).toLocaleString()}
          </p>
        </div>
      </div>

      {signal.why && (
        <div className="rounded-lg border border-white/10 bg-black/30 p-3">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Why it may matter</div>
          <p className="text-sm text-white/85 leading-relaxed">{signal.why}</p>
        </div>
      )}

      {(signal.matched_signals || []).length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Matched signals</div>
          <div className="flex flex-wrap gap-1.5">
            {signal.matched_signals.map((ms, i) => (
              <span key={i}
                    className="text-[11px] px-2 py-0.5 rounded-md bg-white/[0.05] border border-white/10 text-white/80">
                {ms}
              </span>
            ))}
          </div>
        </div>
      )}

      {signal.status === "new" && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="outline"
                  data-testid={`signal-review-${signal.id}`}
                  disabled={!!working}
                  onClick={() => onReview(signal)}>
            <Eye className="w-3.5 h-3.5 mr-1.5" /> Mark reviewed
          </Button>
          <Button size="sm"
                  data-testid={`signal-route-${signal.id}`}
                  disabled={!!working}
                  onClick={() => onRoute(signal)}
                  className="bg-white text-black hover:bg-white/90">
            <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" /> Open in review
          </Button>
          <Button size="sm" variant="ghost"
                  data-testid={`signal-dismiss-${signal.id}`}
                  disabled={!!working}
                  onClick={() => setDismissing(true)}
                  className="text-white/60 hover:text-white">
            <X className="w-3.5 h-3.5 mr-1.5" /> Dismiss
          </Button>
        </div>
      )}

      <Dialog open={dismissing} onOpenChange={setDismissing}>
        <DialogContent className="bg-[#0B0E14] border-white/10 text-white" data-testid={`signal-dismiss-modal-${signal.id}`}>
          <DialogHeader>
            <DialogTitle>Dismiss this signal</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/60">
            Add a short reason (optional) so the audit trail records why this signal was set aside.
          </p>
          <textarea
            data-testid={`signal-dismiss-reason-${signal.id}`}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Not relevant to Compass right now"
            rows={3}
            className="w-full rounded-md bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-white/40"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setDismissing(false)}>Cancel</Button>
            <Button data-testid={`signal-dismiss-confirm-${signal.id}`}
                    onClick={() => { onDismiss(signal, reason); setDismissing(false); setReason(""); }}
                    className="bg-white text-black hover:bg-white/90">
              Dismiss signal
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SignalsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusTab, setStatusTab] = useState("new");
  const [working, setWorking] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await signalsApi.list({ status: statusTab, limit: 200 });
      setSignals(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load signals");
    } finally { setLoading(false); }
  }, [statusTab]);

  useEffect(() => { refresh(); }, [refresh]);

  const onReview = async (sig) => {
    setWorking(sig.id);
    try {
      await signalsApi.review(sig.id);
      toast.success("Signal marked reviewed");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Review failed"); }
    finally { setWorking(null); }
  };

  const onDismiss = async (sig, reason) => {
    setWorking(sig.id);
    try {
      await signalsApi.dismiss(sig.id, reason || "");
      toast.success("Signal dismissed");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Dismiss failed"); }
    finally { setWorking(null); }
  };

  const onRoute = async (sig) => {
    setWorking(sig.id);
    try {
      const r = await signalsApi.route(sig.id);
      const iid = r.data?.intake_item?.id;
      if (iid) {
        // Route opens the Intake review flow. No canonical write yet —
        // the executive still confirms in the review pane.
        nav(`/intake?id=${encodeURIComponent(iid)}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Route failed"); }
    finally { setWorking(null); }
  };

  const counts = useMemo(() => {
    const c = { new: 0, reviewed: 0, routed: 0, dismissed: 0 };
    signals.forEach(s => { if (c[s.status] !== undefined) c[s.status] += 1; });
    return c;
  }, [signals]);

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden max-w-full" data-testid="signals-page">
      <div className="max-w-5xl mx-auto p-6 md:p-10 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-white/70" />
              <h1 className="text-3xl font-display font-semibold tracking-tight">Cross-Entity Signals</h1>
            </div>
            <p className="text-sm text-white/50 mt-1 max-w-2xl">
              Intake submissions that may also matter to entities beyond where they were captured.
              Contributors don't see these — HQ decides whether to route, review, or dismiss.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 border-b border-white/10">
          {STATUS_TABS.map(t => (
            <button key={t.key}
                    data-testid={`signals-tab-${t.key}`}
                    onClick={() => setStatusTab(t.key)}
                    className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
                      statusTab === t.key
                        ? "border-white text-white"
                        : "border-transparent text-white/40 hover:text-white/70"
                    }`}>
              {t.label}
              {statusTab === t.key && signals.length > 0 && (
                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/80">
                  {signals.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-white/40" data-testid="signals-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading signals…
          </div>
        ) : signals.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.01] p-10 text-center"
               data-testid="signals-empty">
            <ShieldAlert className="w-6 h-6 mx-auto text-white/30 mb-2" />
            <p className="text-sm text-white/50">No {statusTab} signals right now.</p>
            <p className="text-xs text-white/30 mt-1">Signals appear when Intake spots a substantive cross-entity relevance.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {signals.map(sig => (
              <SignalCard key={sig.id} signal={sig}
                          onReview={onReview}
                          onDismiss={onDismiss}
                          onRoute={onRoute}
                          working={working === sig.id} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
